import { useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { IRedux } from '@/_store/types';
import discountService from '@/_service/discount.service';
import { useQuery } from '@tanstack/react-query';
import { IDiscount } from '@/_types';
import { useTelegram } from '@/_providers/telegram';
import { INewDiscountParams } from '@/_service/types';
import dayjs from 'dayjs';
import { useActiveBrand } from './useActiveBrand';

export default function useProductDiscount(): {
  discounts: IDiscount[];
} {
  const shipperId = useActiveBrand()
  const [discounts, setDiscounts] = useState<IDiscount[]>([]);

  const { auth, cart, branches, checkout, session, points } = useSelector(
    (state: IRedux) => ({
      auth: state.auth,
      cart: state.cart,
      branches: state.common?.branches,
      checkout: state.checkout,
      session: state.session,
      points: state.common?.points,
    }),
    shallowEqual
  );
  const cartState = cart;
  const branch = branches?.[shipperId];
  const checkoutState = checkout;
  const { order_type, platform_id } = session;

  const { tg_chat_id } = useTelegram();
  const isBot = Boolean(tg_chat_id);

  const products = useMemo(() => {
    let acc = 0;
    // cart endi object: { [brand: string]: { isCheckout, products } }
    // Barcha brand'lardagi productlarni yig'ish
    if (
      cartState?.cart &&
      typeof cartState.cart === 'object' &&
      !Array.isArray(cartState.cart)
    ) {
      const allProducts = Object.values(cartState.cart).flatMap(
        (brandCart: any) => brandCart?.products || []
      );
      for (const product of allProducts) {
        let productTotal =
          (product.price_with_discount || 0) * product.quantity;
        if (product.order_modifiers?.length) {
          product.order_modifiers.forEach((modifier: any) => {
            productTotal +=
              +modifier.modifier_price *
              +(modifier.modifier_quantity * product.quantity);
          });
        }
        acc += productTotal;
      }
    }
    return { totalPrice: acc };
  }, [cartState?.cart]);

  const activePoint = useMemo(
    () => points?.find((point) => point.isActive),
    [points]
  );

  const discountParams = useMemo<INewDiscountParams>(
    () => ({
      delivery_price: 0,
      with_cashback: false,
      for_order_amount: products.totalPrice,
      usable_with_others: undefined,
      discount_for: 'product,order',
      client_id: auth?.user?.id || '',
      order_type_id: order_type?.id,
      branch_ids: branch?.id,
      order_source_id:
        platform_id ||
        (isBot
          ? '02609982-bd15-41e5-ba06-2cdcb356d5fb'
          : '3429e492-5faa-4e17-a19e-b32a4d01b267'),
      payment_type_id:
        checkoutState?.payment_type?.id ||
        '6d7444da-761d-4116-8b04-d6b4bf0c06a5',
      lat: activePoint?.location?.[0],
      long: activePoint?.location?.[1],
      future_time: checkoutState?.future_time
        ? dayjs(checkoutState.future_time).format('YYYY-MM-DD HH:mm:ss')
        : undefined,
    }),
    [
      products.totalPrice,
      auth?.user?.id,
      order_type?.id,
      branch?.id,
      platform_id,
      isBot,
      checkoutState?.payment_type?.id,
      activePoint?.location,
      checkoutState?.future_time,
    ]
  );

  // Get order discount
  const { data: allDiscounts, isSuccess } = useQuery({
    queryKey: ['ALL_DISCOUNTS', discountParams, shipperId],
    queryFn: () => discountService.all(discountParams, shipperId),
    enabled: Boolean(order_type?.id),
    retry: false,
  });

  // Use ref to track previous discounts to prevent infinite loops
  const previousDiscountsRef = useRef<string>('');

  useEffect(() => {
    if (isSuccess && allDiscounts?.data?.discounts) {
      const filtered = allDiscounts.data.discounts.filter(
        (cur) => cur.discount_for === 'product'
      );

      // Create a stable string representation for comparison
      const currentDiscountsKey = JSON.stringify(
        filtered.map((d) => ({
          discount_id: d.discount_id,
          discount_amount: d.discount_amount,
        }))
      );

      // Only update if discounts have actually changed
      if (currentDiscountsKey !== previousDiscountsRef.current) {
        setDiscounts(filtered);
        previousDiscountsRef.current = currentDiscountsKey;
      }
    }
  }, [isSuccess, allDiscounts?.data?.discounts]);

  return { discounts };
}
